import { EmergencyContact } from '../../models/emergency-contact';
import { PayrollFrequency } from '../../models/payroll-info';
import { KeyPair } from '../../models/base';
import { EmployeeLaborAllocation } from '../../models/labor-allocation';
import { EmployeeStatus } from '../../models/person-status';
import { EmployeeDeduction } from '../../models/employee-deductions';
import { EmployeeEarning } from '../../models/employee-earning';
import { EmployeeTaxExemptions } from '../../models/employee-tax';
import {
  EmployeeDirectDeposit,
  EmployeeDirectDepositExtended
} from '../../models/employee-direct-deposit';
import { NgbNavChangeEvent } from '@ng-bootstrap/ng-bootstrap';

export class ClearEmployeeStatuses {
  static readonly type = '[PersonEditor] Clear Employee Statuses';
}

export class ClearSSN {
  static readonly type = '[PersonEditor] Clear SSN';
}

export class LoadPerson {
  static readonly type = '[PersonEditor] Load Person';
  constructor(
    public clientCode: string,
    public personId: number
  ) {}
}

export class LoadPersonalDetailsForm {
  static readonly type = '[PersonEditor] Load Personal Details Form';
}

export class LoadJobDetailsForm {
  static readonly type = '[PersonEditor] Load Job Details Form';
}

export class SetEditorMode {
  static readonly type = '[PersonEditor] Set Editor Mode';
  constructor(public mode: string) {}
}

export class LoadUniversalCompanyConfigs {
  static readonly type = '[PersonEditor] Load Universal Company Configs';
  constructor(public clientCode: string) {}
}

/**
 * Loads enums used by multiple parts of the Person editor.
 */
export class LoadUniversalEnums {
  static readonly type = '[PersonEditor] Load Universal Enums';
  constructor(public clientCode: string) {}
}

export class LoadUniversalPersonLists {
  static readonly type = '[PersonEditor] Load Universal Person Lists';
  constructor(
    public clientCode: string,
    public personId: number | null = null,
    public validate: boolean = false
  ) {}
}

export class RefreshEmployeeStatuses {
  static readonly type = '[PersonEditor] Load Employee Statuses';
  constructor(public clientCode: string) {}
}

export class ResetAddressFields {
  static readonly type = '[PersonEditor] Reset Address Fields';
}

export class SetFederalIdChanged {
  static readonly type = '[PersonEditor] Set Federal Id Changed';
  constructor(public newValue: boolean) {}
}

export class SetMaskFederalId {
  static readonly type = '[PersonEditor] Set Mask Federal Id';
  constructor(public newValue: boolean) {}
}

export class SetPersonHasFederalId {
  static readonly type = '[PersonEditor] Set Person Has Federal Id';
  constructor(
    public newValue: boolean,
    public overrideSaveSafety: boolean = false
  ) {}
}

export class SetReadOnly {
  static readonly type = '[PersonEditor] Set Read Only';
  constructor(public newValue: boolean) {}
}

export class SetTabHasChanged {
  static readonly type = '[PersonEditor] Set Tab Has Changed';
}

export class SetSaveInProgress {
  static readonly type = '[PersonEditor] Set Save In Progress';
  constructor(public newValue: boolean) {}
}

export class SetHeader {
  static readonly type = '[PersonEditor] Set Header';
  constructor(public nameOnly: boolean = false) {}
}

export class ClearEmergencyContacts {
  static readonly type = '[PersonEditor] Clear Emergency Contacts';
}

export class RefreshPersonalDetailsLists {
  static readonly type = '[PersonEditor] Refresh Personal Details Lists';
  constructor(public clientCode: string) {}
}

export class SetEmergencyContacts {
  static readonly type = '[PersonEditor] Set Emergency Contacts';
  constructor(public contacts: EmergencyContact[]) {}
}

export class AddEmergencyContact {
  static readonly type = '[PersonEditor] Add Emergency Contact';
  constructor(public contact: EmergencyContact) {}
}

export class RemoveEmergencyContact {
  static readonly type = '[PersonEditor] Remove Emergency Contact';
  constructor(public contact: EmergencyContact) {}
}

export class EditEmergencyContact {
  static readonly type = '[PersonEditor] Edit Emergency Contact';
  constructor(public contact: EmergencyContact) {}
}

export class RefreshPerson {
  static readonly type = '[PersonEditor] Refresh Person';
  constructor(public clientCode: string) {}
}

export class SetPayFrequencyDetails {
  static readonly type = '[PersonEditor] Set Pay Frequency Details';
  constructor(
    public payFrequency: PayrollFrequency,
    public calendarId: number
  ) {}
}
export class SetCalendarId {
  static readonly type = '[PersonEditor] Set Calendar Id';
  constructor(public calendarId: number) {}
}

export class AddJobCode {
  static readonly type = '[PersonEditor] Add Job Code';
  constructor(public jobCode: KeyPair) {}
}

export class SetLaborAllocations {
  static readonly type = '[PersonEditor] Set Labor Allocations';
  constructor(public laborAllocations: EmployeeLaborAllocation[]) {}
}

export class AddLaborAllocation {
  static readonly type = '[PersonEditor] Add Labor Allocation';
  constructor(public laborAllocation: EmployeeLaborAllocation) {}
}

export class RemoveLaborAllocation {
  static readonly type = '[PersonEditor] Remove Labor Allocation';
  constructor(public laborAllocation: EmployeeLaborAllocation) {}
}

export class UpdateLaborAllocation {
  static readonly type = '[PersonEditor] Update Labor Allocation';
  constructor(
    public laborAllocation: EmployeeLaborAllocation,
    public jobCodeId: number,
    public percentageOfGrossPay: number
  ) {}
}

export class SetAllowDirectDeposit {
  static readonly type = '[PersonEditor] Set Allow Direct Deposit';
  constructor(public allowDirectDeposit: boolean) {}
}

export class AddEmployeeStatus {
  static readonly type = '[PersonEditor] Add Employee Status';
  constructor(public status: EmployeeStatus) {}
}

export class EditEmployeeStatus {
  static readonly type = '[PersonEditor] Edit Employee Status';
  constructor(public status: EmployeeStatus) {}
}

export class SetUseNewHireReporting {
  static readonly type = '[PersonEditor] Set Use New Hire Reporting';
  constructor(public useNewHireReporting: boolean) {}
}

export class SavePerson {
  static readonly type = '[PersonEditor] Save Person';
  constructor(
    public clientCode: string,
    public tabElemIds: any,
    public activeTabId: string
  ) {}
}

export class SetOccupationChanged {
  static readonly type = '[PersonEditor] Set Occupation Changed';
  constructor(public newValue: boolean) {}
}

export class PersonStatusChanged {
  static readonly type = '[PersonEditor] Person Status Changed';
  constructor() {}
}

export class ClearEmploymentTypeCollections {
  static readonly type = '[PersonEditor] Clear Employment Type Collections';
}

export class SetEmployeeDeductions {
  static readonly type = '[PersonEditor] Set Employee Deductions';
  constructor(public deductions: EmployeeDeduction[]) {}
}

export class UpdateEmployeeDeduction {
  static readonly type = '[PersonEditor] Update Deduction';
  constructor(public deduction: EmployeeDeduction) {}
}

export class ReloadEmployeeDeductions {
  static readonly type = '[PersonEditor] Reload Employee Deductions';
  constructor(public clientCode: string) {}
}

export class AddEmployeeDeduction {
  static readonly type = '[PersonEditor] Add Employee Deduction';
  constructor(public deduction: EmployeeDeduction) {}
}

export class ReloadEmployeeEarnings {
  static readonly type = '[PersonEditor] Reload Employee Earnings';
  constructor(public clientCode: string) {}
}

export class SetEmployeeEarnings {
  static readonly type = '[PersonEditor] Set Employee Earnings';
  constructor(public earnings: EmployeeEarning[]) {}
}

export class UpdateEmployeeEarning {
  static readonly type = '[PersonEditor] Update Earning';
  constructor(public earning: EmployeeEarning) {}
}

export class AddEmployeeEarning {
  static readonly type = '[PersonEditor] Add Employee Earning';
  constructor(public earning: EmployeeEarning) {}
}

export class SetTaxExemptions {
  static readonly type = '[PersonEditor] Set Tax Exemptions';
  constructor() {}
}

export class SetTaxes {
  static readonly type = '[PersonEditor] Set Taxes';
  constructor() {}
}

export class ResetTaxesForm {
  static readonly type = '[PersonEditor] Reset Taxes Form';
}

export class BuildLocalTaxForm {
  static readonly type = '[PersonEditor] Build Local Tax Form';
  constructor() {}
}

export class SetEmployeeStateTaxExemptionsByState {
  static readonly type = '[PersonEditor] Set Employee State Tax Exemptions By State';
  constructor() {}
}

export class MarkActiveFormAsTouched {
  static readonly type = '[PersonEditor] Mark Active Form As Touched';

  constructor() {}
}

export class ResetEditorForms {
  static readonly type = '[PersonEditor] Reset Editor Forms';
}

export class MarkFormsPristine {
  static readonly type = '[PersonEditor] Mark Forms Pristine';
}

export class SetPersonalDetails {
  static readonly type = '[PersonEditor] Set Personal Details';
}

export class SetJobDetails {
  static readonly type = '[PersonEditor] Set Job Details';
}

export class InitFormData {
  static readonly type = '[PersonEditor] Init Form Data';
}

export class RefreshPersonLists {
  static readonly type = '[PersonEditor] Refresh Person Lists';
}

export class SetShowVerifyFederalId {
  static readonly type = '[PersonEditor] Set Show Verify Federal Id';
  constructor(public showVerifyFederalId: boolean) {}
}

export class ClearAlertModal {
  static readonly type = '[PersonEditor] Clear Alert Modal';
}

export class SetSaveButtonText {
  static readonly type = '[PersonEditor] Set Save Button Text';
  constructor(public text: string) {}
}

export class TriggerSave {
  static readonly type = '[PersonEditor] Trigger Save';
  constructor(
    public clientCode: string,
    public tabElemIds: any,
    public activeTabId: string
  ) {}
}

export class SetDirectDeposits {
  static readonly type = '[PersonEditor] Set Direct Deposits';
  constructor(public directDeposits: EmployeeDirectDepositExtended[]) {}
}

export class ReloadDirectDeposits {
  static readonly type = '[PersonEditor] Reload Direct Deposits';
}

export class ClearPerson {
  static readonly type = '[PersonEditor] Clear Person';
}

export class ResetEditorState {
  static readonly type = '[PersonEditor] Reset Editor State';
}

export class SetDirectDepositFormDirty {
  static readonly type = '[PersonEditor] Set Direct Deposit Form Dirty';
  constructor(public dirty: boolean) {}
}

export class ResetPersonalDetailsForm {
  static readonly type = '[PersonEditor] Reset Personal Details Form';
}

export class ResetJobDetailsForm {
  static readonly type = '[PersonEditor] Reset Job Details Form';
}

export class SetCurrentTabStatus {
  static readonly type = '[PersonEditor] Set Current Tab Status';
  constructor(public status: string) {}
}
export class SetCurrentTabDirty {
  static readonly type = '[PersonEditor] Set Current Tab Dirty';
  constructor(public newValue: boolean) {}
}
